import { Component, OnInit, Input, NgZone, ChangeDetectorRef } from '@angular/core';
import { CustomerNotificationService } from '../../services/customer-notification.service';
import { CustomerNotificationCategory } from '../../shared/enums/smarthub-enums';
import { SettingsService } from '../../shared/services/settings/settings.service';
import { CustomerNotificationCounts } from '../../services/customer-notification.model';
import { takeUntil } from 'rxjs/operators';
import { BaseObserver } from '../../shared/baseobserver';
import { FeatureToggleService } from '../../shared/services/feature-toggle/feature-toggle.service';
import { Header } from '../header/header.model';
import { HeaderService } from '../header/header.service';
import { ConfigService } from '../../shared/services/config/config.service';
import { UltiProAuthService } from '../../auth/ultiproauth.service';

@Component({
  selector: 'ssg-alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.less']
})
export class AlertsComponent extends BaseObserver implements OnInit {
  CustomerNotificationCategory = CustomerNotificationCategory;
  private countsByCategory: CustomerNotificationCounts[] = [];
  private readonly alertCategories: CustomerNotificationCategory[] = [
    // CustomerNotificationCategory.Unknown,     // suppressed for now; was for testing only
    CustomerNotificationCategory.BankNotificationPay
  ];
  private readonly notificationCategories: CustomerNotificationCategory[] = [];

  /** Override navigation to support being embedded in another application (e.g. psg). */
  @Input() navOverrides: { onNavIn: () => Promise<boolean>; onNavOut: () => Promise<boolean>; headerOverrides: Partial<Header> };
  @Input() usePsgStyle = false;

  get alertCounts(): CustomerNotificationCounts[] {
    if (!this.countsByCategory) {
      return null;
    }
    return this.isFifteenDayAlertsEnabled
      ? this.countsByCategory.filter(c => this.alertCategories.includes(c.category))
      : this.countsByCategory.filter(c => this.alertCategories.includes(c.category) && c.total > 0);
  }

  private get notificationCounts(): CustomerNotificationCounts[] {
    return this.countsByCategory
      ? this.countsByCategory.filter(c => this.notificationCategories.includes(c.category) && c.total > 0)
      : null;
  }

  get numUnAcknowledged(): number {
    return (this.alertCounts && this.notificationCounts)
          ? this.alertCounts.concat(this.notificationCounts).map(c => c.unAcknowledged).reduce((a, b) => a + b, 0) // sum
          : null;
  }

  get showMessageForNoAlerts(): boolean {
    return this.numAlerts < 1 && !this.isFifteenDayAlertsEnabled;
  }

  get categoryI18nKey(): string {
    return this.isFifteenDayAlertsEnabled ? 'ALERTS.VIEW_CATEGORY' : 'ALERTS.RECENTLY_ACKNOWLEDGED';
  }

  get numAlerts(): number {
    return this.alertCounts
         ? this.alertCounts.map(c => c.total).reduce((a, b) => a + b, 0) // sum
         : 0;
  }

  private get isFifteenDayAlertsEnabled(): boolean {
    return this.productConfigService.isFeatureEnabled('enableFifteenDayAlertsAndNotifications');
  }

  constructor(
    private readonly customerNotificationService: CustomerNotificationService,
    private readonly settings: SettingsService,
    private readonly productConfigService: FeatureToggleService,
    private readonly headerService: HeaderService,
    private readonly configService: ConfigService,
    private readonly settingsService: SettingsService,
    private readonly authService: UltiProAuthService,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    super();

  }

  ngOnInit() {
    if (!this.configService.identityEnabled) {
      this.authService.authorize(btoa(this.settingsService.tenantId + ':' + this.settingsService.loginToken));
    }
    this.getAlerts();
  }

  onAlertsClick(category: CustomerNotificationCategory) {
    let headerOverrides = {};
    if (this.navOverrides) {
      headerOverrides = this.navOverrides.headerOverrides;
      const finishNavigation = this._finishAlertsNavigation.bind(this);
      this.navOverrides.onNavIn().then(v => {
        finishNavigation(category, headerOverrides);
      });
    } else {
      this._finishAlertsNavigation(category, headerOverrides);
    }
  }

  private _finishAlertsNavigation(category: CustomerNotificationCategory, headerOverrides: Partial<Header>) {
    this.headerService.navigate([{ outlets: { eepay: ['alertsdetails', { category }] } }], headerOverrides);
    this.headerService.navEvents.next(this.navOverrides.onNavOut);
  }

  private getAlerts() {
    this.customerNotificationService
      .getRecentCountsAllCategories(this.settings.tenantId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((countsByCat: CustomerNotificationCounts[]) => {
        this.countsByCategory = countsByCat.map(c => new CustomerNotificationCounts(c));
        this.changeDetector.detectChanges();
      });
  }
}
